import { GLSLSource } from "../glsl";
import { GLSL_ARG, toDataType } from "../glsl/typeMapping";
import { Output } from "../output";
import { Renderer } from "../renderer";
import { Texture } from "../texture";
import { printError, ucfirst } from "../utils";
import { Generate } from "./generator";
import { TransformFeedback } from "./transformFeedback";
import { VAO } from "./vao";
export const GL_FRAGMENT_SHADER = 0x8b30;
export const GL_VERTEX_SHADER = 0x8b31;
const CLEAR_COLOR_FLOAT = new Float32Array([0, 0, 0, 0]);
const CLEAR_COLOR_UINT = new Uint32Array([0, 0, 0, 0]);
const CLEAR_COLOR_INT = new Int32Array([0, 0, 0, 0]);
const CLEAR_DEPTH = new Float32Array([1]);
export var ShaderVaryingsMode;
(function (ShaderVaryingsMode) {
    ShaderVaryingsMode[ShaderVaryingsMode["IN"] = 1] = "IN";
    ShaderVaryingsMode[ShaderVaryingsMode["OUT"] = 2] = "OUT";
    ShaderVaryingsMode[ShaderVaryingsMode["SHARED"] = 3] = "SHARED";
})(ShaderVaryingsMode || (ShaderVaryingsMode = {}));
export class ShaderError extends Error {
    constructor(message) {
        super(message);
        this.name = "ShaderError";
    }
}
export class Shader {
    constructor(synth, props = {}, skip = false) {
        this.program = null;
        this.uniformsLocation = {};
        this._mrtFBO = null; // FBO per Multiple Render Targets (MRT)
        props = { ...Shader.defaultGydraShader, ...props };
        this.name = props.name;
        this.synth = synth;
        this.gl = synth.renderer.gl;
        this.drawCalls = 0;
        this.needsUpdate = false;
        this.precision = props.precision || synth.precision;
        this.width = props.width ?? synth.width;
        this.height = props.height ?? synth.height;
        this.defines = { ...(props.defines || {}) };
        this._varyings = { ...(props.varyings || {}) };
        this.shaderUniforms = {};
        this._uniforms = Generate.sanitizeUniforms(props.uniforms || []);
        this._autoClear = props.autoClear !== false;
        this.cullFace(props.cullFace);
        this.blend(props.blendMode);
        this._depthTest = props.depthTest;
        if (props.vao)
            this.VAO = props.vao instanceof VAO ? props.vao : new VAO(this.synth, props.vao);
        if (props.transformFeedback) {
            this._transformFeedback =
                props.transformFeedback instanceof TransformFeedback
                    ? props.transformFeedback
                    : new TransformFeedback(this.synth, props.transformFeedback);
        }
        if (typeof props.fragment !== "undefined")
            this.fragment(props.fragment);
        if (props.vertex)
            this.vertex(props.vertex);
        if (!skip)
            synth.shaders.push(this);
    }
    ////////////////////
    compile() {
        if (this.VAO) {
            for (const [name, sb] of Object.entries(this.VAO.attributes)) {
                this.defines["HAS_" + name.toUpperCase()] = true;
                this._varyings[name] = {
                    type: toDataType(sb._size, sb._type),
                    mode: ShaderVaryingsMode.IN,
                    shader: GL_VERTEX_SHADER,
                };
                this._varyings["v" + ucfirst(name)] = {
                    type: toDataType(sb._size, sb._type),
                    mode: ShaderVaryingsMode.SHARED,
                };
            }
            if (this.VAO._instances) {
                this.defines["INSTANCES"] = this.VAO._instances;
            }
            else {
                delete this.defines["INSTANCES"];
            }
        }
        // after defines and varyings are set we can generate shaders
        this.#generateVertex();
        this.#generateFragment();
        try {
            this.#createProgram();
        }
        catch (e) {
            console.error(`[${this.name}] Error creating program for shader`, e, this);
        }
        return this;
    }
    blend(mode) {
        this.blendMode = mode;
        return this;
    }
    cullFace(mode = "back") {
        switch (mode) {
            case "back":
                this._cullFace = this.gl.BACK;
                break;
            case "front":
                this._cullFace = this.gl.FRONT;
                break;
            case "both":
                this._cullFace = this.gl.FRONT_AND_BACK;
                break;
            case "none":
            case false:
                this._cullFace = null;
                break;
            default:
                throw new Error("Invalid cullFace mode: " + mode);
        }
        return this;
    }
    vao(vao) {
        if (vao) {
            this.VAO = vao instanceof VAO ? vao : new VAO(this.synth, vao);
            if (this.program)
                this.VAO.bindVertexAttrs(this);
        }
        else
            this.VAO = null;
        return this;
    }
    transformFeedback(tf) {
        this._transformFeedback = tf instanceof TransformFeedback ? tf : new TransformFeedback(this.synth, tf);
        return this;
    }
    ////////////////////
    #createProgram() {
        if (this.program) {
            this.gl.deleteProgram(this.program);
        }
        this.program = Renderer.createProgram(this.gl, this.vertexShader, this.fragmentShader, this._transformFeedback?.keys(), this._transformFeedback?.mode);
        if (!this.program) {
            throw new Error("Failed to create program for shader: " + this.name);
        }
        if (this.VAO) {
            this.VAO.bindVertexAttrs(this);
        }
        this.uniformsLocation = {};
        return this;
    }
    frag(fragment) {
        return this.fragment(fragment);
    }
    fragment(fragment) {
        this.fragmentSource =
            typeof fragment === "boolean"
                ? //? fragment || !this._transformFeedback
                    Generate.sanitizeShaderString("", this.precision)
                : //	: false // rasterizerDiscard`#version 300 es\nprecision highp float;\nvoid main() {}`
                    typeof fragment === "string"
                        ? Generate.sanitizeShaderString(fragment, this.precision)
                        : fragment instanceof GLSLSource
                            ? fragment.generator()
                            : fragment;
        if (fragment instanceof GLSLSource) {
            this._varyings = { ...Shader.defaultGydraShader.varyings, ...this._varyings };
        }
        return this;
    }
    vert(vertex) {
        return this.vertex(vertex);
    }
    vertex(vertex) {
        this.vertexSource =
            typeof vertex === "string"
                ? Generate.sanitizeShaderString(vertex, this.precision)
                : vertex instanceof GLSLSource
                    ? vertex.generator()
                    : vertex;
        return this;
    }
    #generateFragment() {
        if (this.fragmentSource === false)
            return this;
        const output = this.#outputType();
        const varyings = {
            ...this._varyings,
            fragColor: {
                type: output.fragType,
                mode: ShaderVaryingsMode.OUT,
                shader: GL_FRAGMENT_SHADER,
            },
        };
        const generated = typeof this.fragmentSource !== "string"
            ? Generate.fragment(this.precision, this.fragmentSource, this._uniforms, this.defines, varyings, { outputType: output.type, outputScale: output.scale })
            : this.fragmentSource;
        if (this.fragmentGenerated === generated)
            return this;
        const gl = this.gl;
        if (this.fragmentShader && this.program) {
            gl.detachShader(this.program, this.fragmentShader);
            gl.deleteShader(this.fragmentShader);
        }
        const [fragmentShader, fragmentShaderError] = Renderer.createShader(gl, gl.FRAGMENT_SHADER, generated);
        if (fragmentShaderError) {
            const error = new ShaderError(fragmentShaderError);
            printError(error, generated, this.name + " fragment");
            throw error;
        }
        this.fragmentGenerated = generated;
        this.fragmentShader = fragmentShader;
        // if (this.program) {
        // 	gl.attachShader(this.program, this.fragmentShader)
        // 	gl.linkProgram(this.program)
        // 	if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
        // 		throw new Error("Failed to link program: " + gl.getProgramInfoLog(this.program))
        // 	}
        // }
        return this;
    }
    #generateVertex() {
        const generated = typeof this.vertexSource !== "string"
            ? Generate.vertex(this.precision, this.vertexSource, this._uniforms, this.defines, this._varyings)
            : this.vertexSource;
        if (this.vertexGenerated === generated)
            return this;
        const gl = this.gl;
        if (this.vertexShader && this.program) {
            gl.detachShader(this.program, this.vertexShader);
            gl.deleteShader(this.vertexShader);
        }
        const [vertexShader, vertexShaderError] = Renderer.createShader(gl, gl.VERTEX_SHADER, generated);
        if (vertexShaderError) {
            const error = new ShaderError(vertexShaderError);
            printError(error, generated, this.name + " vertex");
            throw error;
        }
        this.vertexGenerated = generated;
        this.vertexShader = vertexShader;
        // if (this.program) {
        // 	gl.attachShader(this.program, this.vertexShader)
        // 	gl.linkProgram(this.program)
        // 	if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
        // 		throw new Error("Failed to link program: " + gl.getProgramInfoLog(this.program))
        // 	}
        // }
        return this;
    }
    #outputType() {
        const target = this.texture;
        if (!target || !target.isIntegerFormat()) {
            return { type: "float", scale: 1, fragType: "vec4" };
        }
        const type = target.isUnsignedType() ? "uint" : "int";
        return { type, scale: target.integerScale(), fragType: type === "uint" ? "uvec4" : "ivec4" };
    }
    /////////////////////////
    define(toAdd, value) {
        if (typeof toAdd === "string") {
            toAdd = { [toAdd]: value };
        }
        for (const [name, value] of Object.entries(toAdd)) {
            if (value === null) {
                delete this.defines[name];
                continue;
            }
            this.defines[name] = value;
        }
        return this;
    }
    varyngs(v) {
        this._varyings = { ...this._varyings, ...v };
        return this;
    }
    uniforms(uniforms) {
        return this.uniform(uniforms);
    }
    uniform(toAdd, value) {
        toAdd = typeof toAdd === "string" ? { [toAdd]: value } : toAdd;
        const toAddSanitized = Generate.sanitizeUniforms(toAdd);
        for (const u of toAddSanitized) {
            const index = this._uniforms.findIndex(uniform => uniform.name === u.name);
            if (index >= 0) {
                if (u.value === null) {
                    this._uniforms.splice(index, 1);
                    continue;
                }
                this._uniforms[index].value = u.value;
            }
            else if (u !== null) {
                this._uniforms.push(u);
            }
        }
        return this.updateShaderUniforms();
    }
    ////////////////////
    resize(width, height) {
        this.width = width;
        this.height = height;
        if (this.shaderUniforms["resolution"])
            this.shaderUniforms["resolution"] = ["vec2", [width, height]];
        // if (this.texture && !(this.texture instanceof Output)) {
        // 	this.texture.resize(width, height)
        // }
        return this;
    }
    updateShaderUniforms(uniforms = []) {
        const allUniforms = [];
        for (let i = 0, len = uniforms.length; i < len; i++)
            allUniforms.push(uniforms[i]);
        for (let i = 0, len = this._uniforms.length; i < len; i++)
            allUniforms.push(this._uniforms[i]);
        if (typeof this.fragmentSource !== "string" && this.fragmentSource && this.fragmentSource.uniforms)
            for (let i = 0, len = this.fragmentSource.uniforms.length; i < len; i++)
                allUniforms.push(this.fragmentSource.uniforms[i]);
        if (typeof this.vertexSource !== "string" && this.vertexSource && this.vertexSource.uniforms)
            for (let i = 0, len = this.vertexSource.uniforms.length; i < len; i++)
                allUniforms.push(this.vertexSource.uniforms[i]);
        this.shaderUniforms = Generate.uniforms(this.synth, allUniforms);
        if (this.texture && this.uniformsLocation["prevFrame"]) {
            this.shaderUniforms["prevFrame"] = [this.texture.samplerType(), this.texture.texture()];
        }
        return this;
    }
    update(uniforms = []) {
        if (!this.needsUpdate)
            return this;
        this.updateShaderUniforms(uniforms);
        return this.draw(this.texture);
    }
    forceUpdate() {
        const needsUpdate = this.needsUpdate;
        this.needsUpdate = true;
        this.update();
        this.needsUpdate = needsUpdate;
        return this;
    }
    autoClear(autoClear = true) {
        this._autoClear = autoClear;
        return this;
    }
    draw(to = this.texture) {
        if (!this.VAO || !this.program) {
            console.warn(`[${this.name}] ${!this.VAO ? "VAO" : "Program"} not defined`);
            return this;
        }
        const gl = this.gl;
        const targets = Array.isArray(to) ? to : to ? [to] : [];
        let activeFBO = null;
        let targetWidth = this.width;
        let targetHeight = this.height;
        gl.useProgram(this.program);
        const textureUnit = this.bindUniforms(0);
        if (Array.isArray(to)) {
            if (!this._mrtFBO)
                this._mrtFBO = gl.createFramebuffer();
            activeFBO = this._mrtFBO;
            targetWidth = to[0].width;
            targetHeight = to[0].height;
            gl.bindFramebuffer(gl.FRAMEBUFFER, activeFBO);
            const attachments = [];
            for (let i = 0; i < to.length; i++) {
                const texture = to[i];
                const attachment = gl.COLOR_ATTACHMENT0 + i;
                gl.framebufferTexture2D(gl.FRAMEBUFFER, attachment, gl.TEXTURE_2D, texture.texture(), 0);
                attachments.push(attachment);
                texture.updates++;
            }
            gl.drawBuffers(attachments);
        }
        else if (to) {
            to.next();
            to.updates++;
            targetWidth = to.width;
            targetHeight = to.height;
            activeFBO = to.framebuffer();
            gl.bindFramebuffer(gl.FRAMEBUFFER, activeFBO);
        }
        if (this.VAO) {
            if (this.blendMode) {
                gl.enable(gl.BLEND);
                gl.blendFunc(this.blendMode[0], this.blendMode[1]);
            }
            if (this._cullFace) {
                gl.enable(gl.CULL_FACE);
                gl.cullFace(this._cullFace);
            }
            if (this._depthTest) {
                gl.enable(gl.DEPTH_TEST);
            }
            // draw
            this.VAO._rasterizerDiscard && gl.enable(gl.RASTERIZER_DISCARD);
            gl.viewport(0, 0, targetWidth, targetHeight);
            if (this._autoClear) {
                const hasInteger = targets.some(t => t && t.isIntegerFormat());
                if (hasInteger) {
                    targets.forEach((tex, index) => {
                        if (!tex)
                            return;
                        if (tex.isIntegerFormat()) {
                            tex.isUnsignedType()
                                ? gl.clearBufferuiv(gl.COLOR, index, CLEAR_COLOR_UINT)
                                : gl.clearBufferiv(gl.COLOR, index, CLEAR_COLOR_INT);
                        }
                        else {
                            gl.clearBufferfv(gl.COLOR, index, CLEAR_COLOR_FLOAT);
                        }
                    });
                    gl.clearBufferfv(gl.DEPTH, 0, CLEAR_DEPTH);
                }
                else {
                    gl.clearColor(0.0, 0.0, 0.0, 0.0);
                    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
                }
            }
            gl.bindVertexArray(this.VAO.buffer);
            if (this._transformFeedback) {
                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this._transformFeedback._buffer);
                gl.beginTransformFeedback(this.VAO._primitive);
            }
            if (this.VAO.indices)
                gl.bindBuffer(this.VAO.indices._target, this.VAO.indices._buffer);
            if (this.VAO._elements > 0) {
                if (this.VAO._instances > 0)
                    gl.drawElementsInstanced(this.VAO._primitive, this.VAO._elements, this.VAO.indices._type, 0, this.VAO._instances);
                else
                    gl.drawElements(this.VAO._primitive, this.VAO._elements, this.VAO.indices._type, 0);
            }
            else
                gl.drawArrays(this.VAO._primitive, 0, this.VAO._count);
            // clear
            if (this._transformFeedback) {
                gl.endTransformFeedback();
                gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, null);
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
            gl.bindVertexArray(null);
            this.VAO._rasterizerDiscard && gl.disable(gl.RASTERIZER_DISCARD);
            if (this._cullFace) {
                gl.disable(gl.CULL_FACE);
                //gl.cullFace(gl.BACK)
            }
            if (this.blendMode) {
                gl.disable(gl.BLEND);
            }
            if (this._depthTest) {
                gl.disable(gl.DEPTH_TEST);
            }
        }
        for (let u = 0; u < textureUnit; u++) {
            gl.activeTexture(gl.TEXTURE0 + u);
            gl.bindTexture(gl.TEXTURE_2D, null);
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
        }
        if (activeFBO) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        }
        this.drawCalls++;
        return this;
    }
    out(texture) {
        const previousTexture = this.texture;
        if (previousTexture && previousTexture !== texture) {
            previousTexture.shader = null;
        }
        if (texture) {
            // TODO: move to output
            // texture instanceof Output && texture.attachedSequence && texture.attachedSequence.stop()
            if (texture instanceof Output) {
                // check if sequence use output
                this.synth.seq.forEach(seq => {
                    if (seq.output === texture && !seq.layers.find(layer => layer === this)) {
                        seq.stop();
                    }
                });
                texture.activate();
            }
            texture.shader = this;
            this.texture = texture;
        }
        else {
            if (this.texture && this.texture instanceof Output) {
                this.texture.shader = null;
            }
            this.texture = null;
        }
        this.compile();
        return this.forceUpdate();
    }
    /////////////////////////
    clear() {
        this.activate(false);
        if (this.program) {
            this.gl.deleteProgram(this.program);
            this.program = null;
        }
        if (this.vertexShader) {
            this.gl.deleteShader(this.vertexShader);
            this.vertexShader = null;
        }
        if (this.fragmentShader) {
            this.gl.deleteShader(this.fragmentShader);
            this.fragmentShader = null;
        }
        if (this.VAO) {
            if (this.VAO !== Shader.defaultGydraVAO) {
                this.VAO.clear();
            }
            this.VAO = null;
        }
        if (this._transformFeedback) {
            this._transformFeedback.clear();
            this._transformFeedback = null;
        }
        this._uniforms = [];
        this.shaderUniforms = {};
        this.defines = {};
        this._varyings = {};
        this.vertexSource = null;
        this.fragmentSource = null;
        this.vertexGenerated = null;
        this.fragmentGenerated = null;
    }
    // activate update recursively
    activate(active = true, force = false /* used by synth drawers */) {
        if (this.needsUpdate === active && force === false)
            return this;
        this.needsUpdate = active;
        function activateUniforms(uniforms) {
            for (const uniform of uniforms) {
                if (uniform.value instanceof Texture || uniform.value instanceof Shader) {
                    uniform.value.activate(active);
                    if (uniform.value instanceof Output) {
                        uniform.value.shader?.activate(active);
                    }
                }
            }
        }
        if (typeof this.fragmentSource !== "string" && this.fragmentSource) {
            if (this.fragmentSource.uniforms)
                activateUniforms(this.fragmentSource.uniforms);
            if (this.fragmentSource.dependencies)
                this.fragmentSource.dependencies.forEach(shader => shader.activate(true));
        }
        if (typeof this.vertexSource !== "string" && this.vertexSource) {
            if (this.vertexSource.uniforms)
                activateUniforms(this.vertexSource.uniforms);
            if (this.vertexSource.dependencies)
                this.vertexSource.dependencies.forEach(shader => shader.activate(true));
        }
        if (this._uniforms)
            activateUniforms(this._uniforms);
    }
    ////////////////////
    bindUniforms(initialTextureUnit = 0) {
        const gl = this.gl;
        let textureUnit = initialTextureUnit;
        for (let i = 0, uniformKeys = Object.keys(this.shaderUniforms), len = uniformKeys.length; i < len; i++) {
            const uniformKey = uniformKeys[i];
            if (!this.uniformsLocation[uniformKey]) {
                this.uniformsLocation[uniformKey] = gl.getUniformLocation(this.program, uniformKey);
            }
            if (this.uniformsLocation[uniformKey] !== null) {
                this.synth.renderer.bindUniform(gl, uniformKey, this.shaderUniforms[uniformKey][0], this.uniformsLocation[uniformKey], this.shaderUniforms[uniformKey][1], textureUnit);
                if (this.shaderUniforms[uniformKey][1] instanceof WebGLTexture) {
                    textureUnit++;
                }
            }
            /*else {
                console.warn("Uniform not found", uniformKey)
            }*/
        }
        return textureUnit;
    }
    // #clearUnusedUniforms() {
    // 	const gl = this.gl
    // 	for (let i = 0, len = this.uniforms.length; i < len; i++) {
    // 		const uniformKey = this.uniforms[i].name
    // 		if (uniformKey === "prevFrame") continue // prevFrame is always used
    // 		if (gl.getUniformLocation(this.program, uniformKey) === null) {
    // 			console.warn("Uniform not found", uniformKey)
    // 			if (this.vertexGenerator && this.vertexGenerator.uniforms) {
    // 				this.vertexGenerator.uniforms = this.vertexGenerator.uniforms.filter(uniform => uniform.name !== uniformKey)
    // 				this.updateGenerators()
    // 			}
    // 			if (this.fragmentGenerator && this.fragmentGenerator.uniforms) {
    // 				this.fragmentGenerator.uniforms = this.fragmentGenerator.uniforms.filter(
    // 					uniform => uniform.name !== uniformKey
    // 				)
    // 				this.updateGenerators()
    // 			}
    // 			delete this.uniforms[uniformKey]
    // 			delete this.uniformsLocation[uniformKey]
    // 		}
    // 	}
    // }
    //////////////////
    // created by renderer
    static { this.defaultGydraShader = {
        vertex: { value: () => `vec4(${GLSL_ARG.st}, 1.0)` },
        varyings: {
            fragColor: {
                type: "vec4",
                mode: ShaderVaryingsMode.OUT,
                shader: GL_FRAGMENT_SHADER,
            },
        },
        uniforms: {},
        defines: {},
        name: "default",
        //blendMode: [this.synth.renderer.gl.SRC_ALPHA, this.synth.renderer.gl.ONE_MINUS_SRC_ALPHA],
        blendMode: [0x0302, 0x0303], // SRC_ALPHA, ONE_MINUS_SRC_ALPHA
        // cullFace: "back",
        // depthTest: true,
    }; }
    static { this.defaultGydraVAO = null; }
}
